import React from 'react';

import { fetchStatus, listAffiliates, listCampaigns } from '../affiliates/api';

/** One affiliate or campaign as the filters name it: its number in the affiliates platform (the `aff_id` part its links
 *  carry) and its name. */
export type DirectoryEntry = { value: string; name: string; /** The platform's own id (affiliates only), for reads keyed by it. */ id?: string };

export type AffiliateDirectory = {
  /** `idle`: not asked (no brand, or nothing needs it yet); `error`: the first read failed (a later one keeps the last
   *  good lists). */
  status: 'idle' | 'loading' | 'ready' | 'error';
  affiliates: DirectoryEntry[];
  campaigns: DirectoryEntry[];
  /** The brand's id in the affiliates platform (from the status read), when it has one. */
  brandId?: string;
};

type Lists = Pick<AffiliateDirectory, 'affiliates' | 'campaigns' | 'brandId'>;

const EMPTY: Lists = { affiliates: [], campaigns: [] };

/** The lists per brand, read once a session (only good answers are kept, so a failed read is tried again). */
const cache = new Map<string, Lists>();

const byName = (a: DirectoryEntry, b: DirectoryEntry): number => a.name.localeCompare(b.name);

const readLists = async (casinoInstanceId: string): Promise<Lists> => {
  // Only for an owner already set up there: listing provisions the affiliates platform's records (an org, a user, a
  // brand) for one who is not, and a brand without the platform refuses with an error. The status read provisions
  // nothing (as Acquisition and Affiliates check it first).
  const status = await fetchStatus(casinoInstanceId);
  if (!status.enabled || !status.user) return EMPTY;
  const [affiliates, campaigns] = await Promise.all([
    listAffiliates(casinoInstanceId),
    listCampaigns(casinoInstanceId),
  ]);
  return {
    // Only those with a number: it is what their links' `aff_id` carries.
    affiliates: affiliates
      .filter((a) => typeof a.affiliate_no === 'number')
      .map((a) => ({
        value: String(a.affiliate_no),
        name: a.display_name?.trim() || a.email || `#${a.affiliate_no}`,
        id: a.id,
      }))
      .sort(byName),
    campaigns: campaigns
      .filter((c) => typeof c.campaign_no === 'number')
      .map((c) => ({ value: String(c.campaign_no), name: c.name?.trim() || `#${c.campaign_no}` }))
      .sort(byName),
    brandId: status.brand?.id,
  };
};

/**
 * The brand's affiliates and campaigns from the affiliates platform, to name the Website tab's affiliate and campaign
 * filters. Read only while `enabled` (the filters sheet is open, or one of them is on), once per brand; a failed read is
 * tried again when `retry` changes (the sheet opening again).
 */
export const useAffiliateDirectory = (
  casinoInstanceId: string | undefined,
  enabled: boolean,
  retry?: unknown,
): AffiliateDirectory => {
  const [state, setState] = React.useState<AffiliateDirectory>(() => {
    const held = casinoInstanceId ? cache.get(casinoInstanceId) : undefined;
    return held ? { status: 'ready', ...held } : { status: 'idle', ...EMPTY };
  });

  // Syncs with the affiliates platform: the brand's lists, once.
  React.useEffect(() => {
    if (!casinoInstanceId) {
      setState({ status: 'idle', ...EMPTY });
      return undefined;
    }
    const held = cache.get(casinoInstanceId);
    if (held) {
      setState({ status: 'ready', ...held });
      return undefined;
    }
    if (!enabled) {
      setState({ status: 'idle', ...EMPTY });
      return undefined;
    }
    let live = true;
    setState({ status: 'loading', ...EMPTY });
    readLists(casinoInstanceId)
      .then((lists) => {
        cache.set(casinoInstanceId, lists);
        if (live) setState({ status: 'ready', ...lists });
      })
      .catch(() => {
        if (live) setState({ status: 'error', ...EMPTY });
      });
    return () => {
      live = false;
    };
  }, [casinoInstanceId, enabled, retry]);

  return state;
};
